import { useWorkOrderPriceDiferenceList } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { WorkOrderPriceDiference } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDisclosure } from '@/hooks';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Dropdown,
  Empty,
  Modal,
  Skeleton,
  Space,
  Table,
  Tooltip,
} from 'antd';
import React from 'react';
import { FaExclamationTriangle, FaQuestionCircle } from 'react-icons/fa';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { ColumnsType } from 'antd/lib/table';

const ComplementDiffPriceButton: React.FC = () => {
  const [modalOpened, modalAction] = useDisclosure();
  const canViewPartCostPrice = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_VIEW_PART_COST_PRICE,
  ]);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { companyConfig },
  } = useWorkorderDetailContext();

  const { data, isLoading } = useWorkOrderPriceDiferenceList(
    {
      companyId: workorder?.company.id,
      complementId: complement?.id,
    },
    modalOpened,
  );

  const dataFormatted =
    data?.map((item) => {
      if (canViewPartCostPrice) {
        return {
          ...item,
          refactoringMargin: item.priceCost * item.refactoringMargin,
        };
      }

      return {
        purchaseCod: item.purchaseCod,
        description: item.description,
        quantity: item.quantity,
        salePrice: item.salePrice,
        partId: item.partId,
        priceCost: 0,
        priceDiference: 0,
        refactoringMargin: 0,
        minimumValue: 0,
        grossPrice: 0,
        grossPriceDiference: 0,
      };
    }) || [];

  const rowTotal = dataFormatted?.reduce(
    (acc, curr) => ({
      purchaseCod: 'Total',
      description: '',
      quantity: (acc.quantity || 0) + curr.quantity,
      priceCost: (acc.priceCost || 0) + curr.priceCost,
      salePrice: (acc.salePrice || 0) + curr.salePrice,
      priceDiference: (acc.priceDiference || 0) + curr.priceDiference,
      refactoringMargin: (acc.refactoringMargin || 0) + curr.refactoringMargin,
      minimumValue: (acc.minimumValue || 0) + curr.minimumValue,
      partId: 0,
      grossPrice: (acc.grossPrice || 0) + (curr.grossPrice || 0),
      grossPriceDiference:
        (acc.grossPriceDiference || 0) + (curr.grossPriceDiference || 0),
    }),
    {} as WorkOrderPriceDiference,
  );

  const renderPrice = (value?: number) => {
    const price = value || 0;
    const color = price < 0 ? 'red' : 'inherit';

    return <span style={{ color }}>{formatterPrice(price)}</span>;
  };

  const purchaseCodColumn: ColumnsType<WorkOrderPriceDiference>[number] = {
    width: 160,
    dataIndex: 'purchaseCod',
    title: 'Código da compra',
    render: (code) =>
      code || (
        <Space>
          <span>-</span>
          <Tooltip title="Não existe uma compra registrada para este item da O.S.">
            <FaExclamationTriangle />
          </Tooltip>
        </Space>
      ),
  };

  const salePriceColumn: ColumnsType<WorkOrderPriceDiference>[number] = {
    width: 160,
    align: 'right',
    dataIndex: 'salePrice',
    title: 'Preço de venda($)',
    render: renderPrice,
  };

  const basicColumns: ColumnsType<WorkOrderPriceDiference> = [
    purchaseCodColumn,
    {
      width: 160,
      dataIndex: 'description',
      title: 'Descrição',
      ellipsis: true,
    },
    {
      width: 160,
      align: 'right',
      dataIndex: 'quantity',
      title: 'Qtde.',
    },
  ];

  const costColumns: ColumnsType<WorkOrderPriceDiference> = [
    {
      width: 160,
      align: 'right',
      dataIndex: 'priceCost',
      title: 'Preço de compra($)',
      render: (value) =>
        typeof value === 'number' ? (
          formatterPrice(value, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })
        ) : (
          <Space>
            <span>-</span>
            <Tooltip title="Não existe preço de compra registrada para este item da O.S.">
              <FaExclamationTriangle />
            </Tooltip>
          </Space>
        ),
    },
    salePriceColumn,
    {
      width: 160,
      align: 'right',
      dataIndex: 'priceDiference',
      title: 'Dif. de preço($)',
      render: renderPrice,
    },
    {
      width: 160,
      align: 'right',
      dataIndex: 'grossPrice',
      title: 'Custo bruto($)',
      render: renderPrice,
    },
    {
      width: 160,
      align: 'right',
      dataIndex: 'grossPriceDiference',
      title: 'Diferença bruta($)',
      render: renderPrice,
    },
    {
      width: 160,
      align: 'right',
      dataIndex: 'refactoringMargin',
      title: 'Margem de ref.($)',
      render: renderPrice,
    },
    {
      width: 160,
      align: 'right',
      dataIndex: 'minimumValue',
      render: renderPrice,
      title: (
        <Space>
          <span>Valor mín. ($)</span>
          <Dropdown
            overlay={
              <Card
                title="O que é valor mínimo?"
                style={{ width: 240, borderRadius: 10 }}
              >
                <span>
                  Este é o valor (total) minímo de venda para o item, que é
                  igual ao preço custo mais a marge de refatoramento.
                </span>
              </Card>
            }
          >
            <FaQuestionCircle />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const columns = canViewPartCostPrice
    ? [...basicColumns, ...costColumns]
    : [...basicColumns, salePriceColumn];

  return (
    <>
      <Col span={24} md={12} lg={4} xxl={3}>
        <Button block onClick={modalAction.open}>
          Dif. de preço
        </Button>
      </Col>

      <Modal
        open={modalOpened}
        onCancel={modalAction.close}
        title="Diferença de preço"
        width="80%"
        footer={null}
      >
        <Descriptions
          column={{ lg: 4, md: 2, sm: 1, xs: 1 }}
          layout="vertical"
          labelStyle={{ opacity: 0.7 }}
          contentStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="Cliente">
            {workorder?.customer?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Empresa">
            {workorder?.company.name}
          </Descriptions.Item>
          {canViewPartCostPrice && (
            <Descriptions.Item label="Margem de ref. (%)">
              {formatterPercent(companyConfig?.profitMargin || 0)}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="OS">{workorder?.number}</Descriptions.Item>
        </Descriptions>

        <Divider />

        {isLoading && <Skeleton loading />}

        {!data?.length && !isLoading && (
          <Empty description="Não há peças para este orçamento ou são todas fornecidas" />
        )}

        {!!data?.length && !isLoading && (
          <Table
            dataSource={[...dataFormatted, rowTotal]}
            pagination={false}
            scroll={{ x: true }}
            columns={columns}
          />
        )}
      </Modal>
    </>
  );
};

export default ComplementDiffPriceButton;
